import { defineStore } from 'pinia';
import {
  TitledObject,
  getBookReference as getBookReferenceApi,
  getBookReferenceYears as getBookReferenceYearsApi,
  getReferenceBookTermSearch as getReferenceBookTermSearchApi,
  getBookReferenceYearIssue as getBookReferenceYearIssueApi,
  getReferencesArticle as getReferencesArticleApi,
  getReferencesArticles as getReferencesArticlesApi,
} from 'src/services/TrucodeReferencesService';
import { ref, computed } from 'vue';
import { getUserPreference, setUserPreference } from 'src/services/UserSettingsService';
import { ReferencesState } from 'src/contracts/encoderState';

export interface ReferencesHistoryData {
  id: string,
  bookCode?: string,
  bookDescription?: string,
  articleId?: string,
  articleDescription?: string,
  searchTerm?: string,
}

export const useTrucodeReferenceStore = defineStore('trucodereferencestore', () => {

  function getReferencesState(): ReferencesState {
    return {
      mode: mode.value,
      currentSearchBook: currentSearchBook.value,
      currentBrowseBook: currentBrowseBook.value,
      currentTerm: currentTerm.value,
      browseNodes: browseNodes.value,
      browseExpandedNodes: browseExpandedNodes.value,
      browseSelectedArticleNode: browseSelectedArticleNode.value,
      articleSearchResults: articleSearchResults.value,
      currentArticle: currentArticle.value
    };
  }

  function setReferencesState(referencesState: ReferencesState): void {
    mode.value = referencesState.mode;
    currentSearchBook.value = referencesState.currentSearchBook
    currentBrowseBook.value = referencesState.currentBrowseBook;
    currentTerm.value = referencesState.currentTerm;
    browseNodes.value = referencesState.browseNodes;
    browseExpandedNodes.value = referencesState.browseExpandedNodes;
    browseSelectedArticleNode.value = referencesState.browseSelectedArticleNode;
    articleSearchResults.value = referencesState.articleSearchResults;
    currentArticle.value = referencesState.currentArticle;

  }

  const mode = ref<'search' | 'browse'>('search');
  const getMode = computed(() => mode.value);
  const currentSearchBook = ref<TitledObject>();
  const currentBrowseBook = ref<TitledObject>();
  const currentTerm = ref<string>('');
  function setCurrentSearchBook(book: TitledObject) {
    currentSearchBook.value = book;
    articleSearchResults.value = [];
  }

  const browseNodes = ref<TitledObject[]>([]);
  const browseExpandedNodes = ref<TitledObject[]>([]);
  const browseSelectedArticleNode = ref<TitledObject | null>(null);

  function setCurrentBrowseBook(book: TitledObject) {
    currentBrowseBook.value = book;
    browseNodes.value = [];
    browseExpandedNodes.value = []
    browseSelectedArticleNode.value = null;
  }

  const currentArticle = ref<ReferencesHistoryData | undefined>(undefined);
  function setCurrentArticle(article: TitledObject) {
    currentArticle.value = {
      id: article.id,
      bookCode: currentSearchBook?.value?.id,
      bookDescription: currentSearchBook?.value?.title,
      articleId: article.id,
      articleDescription: article.title
    };
  }

  function nextArticle() {
    navigateArticle(true);
  }

  function previousArticle() {
    navigateArticle(false);
  }

  function navigateArticle(isNext: boolean) {
    const maxArticles = articleSearchResults.value?.length ?? 0;
    const currentArticleIndex = articleSearchResults.value?.findIndex(item => item.id === currentArticle.value?.articleId) ?? 0
    const nextArticle = nextArticleIndex(currentArticleIndex, maxArticles, isNext);
    if (articleSearchResults.value) {
      setCurrentArticle(articleSearchResults.value[nextArticle]);
    }
  }

  function nextArticleIndex(currentArticleIndex: number, maxArticles: number, isNext: boolean) {
    if (isNext) {
      return currentArticleIndex >= maxArticles - 1 ? 0 : currentArticleIndex + 1;
    } else {
      return currentArticleIndex <= 0 ? maxArticles - 1 : currentArticleIndex - 1;
    }
  }

  const articleSearchResults = ref<TitledObject[] | undefined>([]);
  const highlightMatchedTerms = ref<boolean>(false);
  const searchBookReferences = ref<TitledObject[] | undefined>(undefined);
  const browseBookReferences = ref<TitledObject[] | undefined>(undefined);
  const bookReferenceYearsMap = ref<Map<string, number[] | undefined> | undefined>(undefined);
  const bookReferenceYearIssueMap = ref<Map<[string, number], TitledObject[] | undefined> | undefined>(undefined);
  const referencesArticlesResponseMap = ref<Map<number, TitledObject[] | undefined> | undefined>(undefined);
  const referenceBookTermSearchMap = ref<Map<[string, string], TitledObject[]> | undefined>(undefined);

  async function getSearchBookReference(): Promise<TitledObject[]> {
    if (!searchBookReferences.value) {
      searchBookReferences.value = await getBookReferenceApi('SEARCH');
    }

    return searchBookReferences.value;
  }

  async function getBrowseBookReference(): Promise<TitledObject[] | undefined> {
    if (!browseBookReferences.value) {
      browseBookReferences.value = await getBookReferenceApi('LIST');
    }

    return browseBookReferences.value;
  }

  async function getBookReferenceYears(bookId: string): Promise<number[] | undefined> {
    if (!bookReferenceYearsMap.value) {
      bookReferenceYearsMap.value = new Map<string, number[]>();
    }

    let result = bookReferenceYearsMap.value.get(bookId);

    if (!result) {
      result = await getBookReferenceYearsApi(bookId);
      bookReferenceYearsMap.value.set(bookId, result);
    }

    return result;
  }

  async function getBookReferenceYearIssue(year: number): Promise<TitledObject[] | undefined> {
    if (!bookReferenceYearIssueMap.value) {
      bookReferenceYearIssueMap.value = new Map<[string, number], TitledObject[] | undefined>();
    }

    let result = bookReferenceYearIssueMap.value.get([<string>currentBrowseBook?.value?.id, year]);

    if (!result) {
      result = await getBookReferenceYearIssueApi(<string>currentBrowseBook?.value?.id, year);
      bookReferenceYearIssueMap.value.set([<string>currentBrowseBook?.value?.id, year], result);
    }

    return result;
  }

  async function getReferencesArticle(articleId: string): Promise<string | undefined> {
    return await getReferencesArticleApi(articleId);
  }

  async function getReferencesArticles(issueId: number): Promise<TitledObject[] | undefined> {
    if (!referencesArticlesResponseMap.value) {
      referencesArticlesResponseMap.value = new Map<number, TitledObject[]>();
    }

    let result = referencesArticlesResponseMap.value.get(issueId);

    if (!result) {
      result = await getReferencesArticlesApi(issueId);
      referencesArticlesResponseMap.value.set(issueId, result);
    }

    return result;
  }

  async function getReferenceBookTermSearch(term: string): Promise<TitledObject[]> {
    currentTerm.value = term;
    if (!referenceBookTermSearchMap.value) {
      referenceBookTermSearchMap.value = new Map<[string, string], TitledObject[]>();
    }
    const bookId = <string>currentSearchBook?.value?.id;
    let result = referenceBookTermSearchMap.value.get([bookId, term]);

    if (!result) {
      result = await getReferenceBookTermSearchApi(bookId, term);
      referenceBookTermSearchMap.value.set([bookId, term], result);
    }

    articleSearchResults.value = result;

    return result;
  }

  function getCurrentTerm() {
    return currentTerm.value;
  }

  async function updateHighlightMatchedTerms(value: boolean) {
    await setUserPreference('highlightMatchedTerms', `${value}`);
    highlightMatchedTerms.value = value;
  }

  async function getHighlightMatchedTermsValueTerms() {
    const highlightMatchedTermsValue = await getUserPreference('highlightMatchedTerms', true);
    highlightMatchedTerms.value = !!highlightMatchedTermsValue;
  }

  return {
    getReferencesState,
    setReferencesState,
    mode,
    getMode,
    currentTerm,
    highlightMatchedTerms,
    currentSearchBook,
    browseNodes,
    browseExpandedNodes,
    browseSelectedArticleNode,
    setCurrentSearchBook,
    currentBrowseBook,
    setCurrentBrowseBook,
    currentArticle,
    setCurrentArticle,
    nextArticle,
    previousArticle,
    articleSearchResults,
    updateHighlightMatchedTerms,
    getCurrentTerm,
    getHighlightMatchedTermsValueTerms,
    getSearchBookReference,
    getBrowseBookReference,
    getBookReferenceYears,
    getBookReferenceYearIssue,
    getReferencesArticle,
    getReferencesArticles,
    getReferenceBookTermSearch
  }
})
